import React, { useState, useEffect } from 'react'
import {
  Star,
  X,
  CarFront,
  Calendar,
  CheckCircle2,
  Sparkles,
  ArrowRight,
} from 'lucide-react'
import type { UserTrip } from '../api/profile/userProfileApi'
import {
  apiSubmitTripReview,
  dismissReviewForSession,
  type TripReview,
} from '../utils/tripReview'

interface TripReviewModalProps {
  trip: UserTrip
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const RATING_LABELS: Record<number, string> = {
  1: 'Disappointing experience',
  2: 'Could be improved',
  3: 'Good, met expectations',
  4: 'Very good! Enjoyed the ride',
  5: 'Outstanding! Five-star service',
}

const COMPLIMENT_TAGS = [
  '⚡ Punctual & on-time',
  '✨ Clean & pristine vehicle',
  '🛡️ Smooth & safe driving',
  '🤝 Friendly chauffeur',
  '💼 Assisted with luggage',
  '🎶 Pleasant atmosphere',
]

export const TripReviewModal: React.FC<TripReviewModalProps> = ({
  trip,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [rating, setRating] = useState<number>(5)
  const [hoverRating, setHoverRating] = useState<number>(0)
  const [selectedTags, setSelectedTags] = useState<string[]>([
    '⚡ Punctual & on-time',
    '✨ Clean & pristine vehicle',
  ])
  const [feedback, setFeedback] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setIsSuccess(false)
      setIsSubmitting(false)
      setRating(5)
      setFeedback('')
      setSelectedTags([
        '⚡ Punctual & on-time',
        '✨ Clean & pristine vehicle',
      ])
    }
  }, [isOpen])

  if (!isOpen) return null

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const handleDismiss = () => {
    dismissReviewForSession(trip.id)
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (rating === 0) return

    setIsSubmitting(true)

    const review: TripReview = {
      tripId: trip.id,
      bookingReference: trip.bookingReference || trip.reference,
      rating,
      feedback: feedback.trim(),
      tags: selectedTags,
      route: {
        from: trip.from,
        to: trip.to,
      },
      vehicle: trip.vehicle,
      customerName: trip.customerName,
      customerEmail: trip.email,
      submittedAt: new Date().toISOString(),
    }

    try {
      await apiSubmitTripReview(review)
    } finally {
      setIsSubmitting(false)
      setIsSuccess(true)
      onSuccess?.()

      setTimeout(() => {
        onClose()
      }, 2400)
    }
  }

  const activeRating = hoverRating || rating

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 sm:p-6 notranslate">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/55 backdrop-blur-[4px] transition-opacity duration-300 animate-in fade-in"
        onClick={handleDismiss}
      />

      {/* Modal Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-title"
        className="relative w-full max-w-[540px] rounded-[24px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_rgba(0,0,0,0.22)] border border-slate-100 z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-5 top-5 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          aria-label="Close review"
        >
          <X className="h-5 w-5" />
        </button>

        {isSuccess ? (
          /* Success Screen */
          <div className="py-8 text-center flex flex-col items-center justify-center animate-in zoom-in-95 duration-300">
            <div className="relative mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shadow-inner">
              <CheckCircle2 className="h-12 w-12 stroke-[2.2]" />
              <div className="absolute -top-1 -right-1">
                <Sparkles className="h-6 w-6 text-amber-400 animate-bounce" />
              </div>
            </div>

            <h3 className="font-host text-[24px] font-bold text-slate-900 tracking-tight">
              Thank You for Your Feedback!
            </h3>
            <p className="mt-2 text-[15px] text-slate-500 max-w-sm">
              Your review for{' '}
              <span className="font-semibold text-slate-700">
                {trip.reference}
              </span>{' '}
              has been recorded. It helps us ensure premium transfer standards for every passenger.
            </p>

            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-slate-900 px-8 font-host text-[14px] font-medium text-white transition hover:bg-slate-800"
            >
              Done
            </button>
          </div>
        ) : (
          /* Review Form */
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-amber-50 text-amber-500 shadow-[0_2px_10px_rgba(245,158,11,0.15)]">
                <Star className="h-6 w-6 fill-amber-400 stroke-amber-500" />
              </div>
              <div>
                <h2
                  id="review-title"
                  className="font-host text-[20px] sm:text-[22px] font-bold tracking-tight text-slate-900 leading-snug"
                >
                  How was your recent trip?
                </h2>
                <p className="font-host text-[13px] text-slate-500">
                  Rate your experience with Salzburg Cab
                </p>
              </div>
            </div>

            {/* Trip Info Card */}
            <div className="rounded-2xl border border-slate-100 bg-[#F8FAFC] p-3.5">
              <div className="flex items-center justify-between text-[11px] font-semibold tracking-wider text-slate-400 uppercase mb-2">
                <span>Trip Details</span>
                <span className="bg-white px-2 py-0.5 rounded-full border border-slate-200 text-slate-700 font-mono">
                  {trip.reference}
                </span>
              </div>

              <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-800">
                <span className="truncate max-w-[180px]">{trip.from}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="truncate max-w-[180px]">{trip.to}</span>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-slate-500 font-medium">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  {trip.date} • {trip.time}
                </span>
                {trip.vehicle && (
                  <span className="inline-flex items-center gap-1">
                    <CarFront className="h-3.5 w-3.5 text-slate-400" />
                    {trip.vehicle}
                  </span>
                )}
              </div>
            </div>

            {/* Interactive Stars */}
            <div className="text-center py-2">
              <div className="flex items-center justify-center gap-2">
                {[1, 2, 3, 4, 5].map((starValue) => {
                  const isFilled = starValue <= activeRating
                  return (
                    <button
                      key={starValue}
                      type="button"
                      onClick={() => setRating(starValue)}
                      onMouseEnter={() => setHoverRating(starValue)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 transition-transform hover:scale-125 active:scale-95 focus:outline-none"
                      aria-label={`Rate ${starValue} stars`}
                    >
                      <Star
                        className={`h-9 w-9 transition-colors duration-150 ${
                          isFilled
                            ? 'fill-amber-400 stroke-amber-500 text-amber-400'
                            : 'stroke-slate-300 text-transparent'
                        }`}
                      />
                    </button>
                  )
                })}
              </div>

              <p className="mt-2 font-host text-[13px] font-semibold text-amber-600 transition-all">
                {RATING_LABELS[activeRating] || 'Tap a star to rate'}
              </p>
            </div>

            {/* Quick Compliment Tags */}
            <div>
              <label className="block font-host text-[12px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                What went well?
              </label>
              <div className="flex flex-wrap gap-2">
                {COMPLIMENT_TAGS.map((tag) => {
                  const isSelected = selectedTags.includes(tag)
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`inline-flex items-center rounded-full px-3 py-1.5 text-[12px] font-medium transition-all ${
                        isSelected
                          ? 'bg-[#1E50BA] text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
                      }`}
                    >
                      {tag}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Textarea */}
            <div>
              <label
                htmlFor="review-comment"
                className="block font-host text-[12px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5"
              >
                Comments (optional)
              </label>
              <textarea
                id="review-comment"
                rows={3}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Tell us about the vehicle condition, chauffeur professionalism, or trip punctuality..."
                className="w-full rounded-xl border border-slate-200 bg-white p-3 font-host text-[13px] text-slate-800 placeholder-slate-400 focus:border-[#1E50BA] focus:outline-none focus:ring-2 focus:ring-[#1E50BA]/15 transition"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleDismiss}
                className="h-11 rounded-full px-5 font-host text-[13px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition"
              >
                Maybe Later
              </button>

              <button
                type="submit"
                disabled={isSubmitting || rating === 0}
                className="inline-flex h-11 items-center justify-center rounded-full bg-[#1E50BA] px-7 font-host text-[14px] font-semibold text-white shadow-sm hover:bg-[#18429b] active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default TripReviewModal
